import { useEffect, useMemo, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { catalogService, CatalogItem } from '../../services/catalog.service';
import {
  balanceService,
  BalanceRow,
  BalanceSummary,
  BalanceFilters,
} from '../../services/balance.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';

const fc = (n: number) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  }).format(n || 0);

export function BalancesPage() {
  const { showSuccess, showError } = useToast();
  const [filters, setFilters] = useState<BalanceFilters>({ only_with_balance: false });
  const [balances, setBalances] = useState<BalanceRow[]>([]);
  const [summary, setSummary] = useState<BalanceSummary | null>(null);
  const [currencyTypes, setCurrencyTypes] = useState<CatalogItem[]>([]);
  const [departments, setDepartments] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupByOwner, setGroupByOwner] = useState(true);

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    loadData();
  }, [
    filters.owner_type,
    filters.currency_type_id,
    filters.department_id,
    filters.search,
    filters.only_with_balance,
  ]);

  const loadCatalogs = async () => {
    try {
      const [curr, deps] = await Promise.all([
        catalogService.list('currency_types'),
        catalogService.list('departments'),
      ]);
      setCurrencyTypes(curr);
      setDepartments(deps);
    } catch (e) {
      console.error(e);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, sum] = await Promise.all([
        balanceService.listBalances(filters),
        balanceService.getSummary(filters),
      ]);
      setBalances(rows);
      setSummary(sum);
    } catch (e: any) {
      setError(e?.message || 'Error al cargar saldos');
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key: keyof BalanceFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const clearFilters = () => {
    setFilters({ only_with_balance: false });
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.owner_type) count++;
    if (filters.currency_type_id) count++;
    if (filters.department_id) count++;
    if (filters.search) count++;
    if (filters.only_with_balance) count++;
    return count;
  }, [filters]);

  const grouped = useMemo(() => {
    const map = new Map<string, {
      owner_id: string;
      owner_type: 'user' | 'department' | 'location';
      owner_name: string;
      department_name?: string;
      wallets: BalanceRow[];
    }>();
    for (const row of balances) {
      const key = `${row.owner_type}:${row.owner_id}`;
      if (!map.has(key)) {
        map.set(key, {
          owner_id: row.owner_id,
          owner_type: row.owner_type,
          owner_name: row.owner_name,
          department_name: row.owner_department_name,
          wallets: [],
        });
      }
      map.get(key)!.wallets.push(row);
    }
    return Array.from(map.values()).sort((a, b) => {
      const order = { user: 0, department: 1, location: 2 };
      if (order[a.owner_type] !== order[b.owner_type]) return order[a.owner_type] - order[b.owner_type];
      return a.owner_name.localeCompare(b.owner_name);
    });
  }, [balances]);

  const exportCSV = () => {
    const headers = ['Tipo', 'Nombre', 'Departamento', 'Moneda', 'Saldo', 'Actualizado'];
    const rows = balances.map((r) => [
      r.owner_type === 'user' ? 'Usuario' : r.owner_type === 'department' ? 'Departamento' : 'Ubicación',
      r.owner_name,
      r.owner_department_name || '',
      r.currency_name,
      r.balance.toFixed(2),
      new Date(r.updated_at).toLocaleString('es-MX'),
    ]);
    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `saldos_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSuccess('CSV exportado');
  };

  const getOwnerIcon = (type: string) => {
    return type === 'user' ? '👤' : type === 'department' ? '🏢' : '📍';
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Saldos</h1>
          <p className="text-xs text-slate-500">Vista consolidada de wallets</p>
        </div>
        <button
          onClick={exportCSV}
          disabled={balances.length === 0}
          className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold disabled:opacity-50"
        >
          📥 CSV
        </button>
      </div>

      {error && (
        <ErrorState message={error} onRetry={loadData} />
      )}

      {summary && summary.currencies.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-3">
          <p className="text-xs font-bold mb-2">📊 Resumen Consolidado</p>
          <div className="space-y-1.5">
            {summary.currencies.map((curr) => (
              <div
                key={curr.currency_type_id}
                className="flex items-center justify-between bg-slate-50 rounded-lg p-2"
              >
                <p className="text-xs text-slate-600">{curr.currency_name}</p>
                <p className="text-sm font-bold text-primary">{fc(curr.total)}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-500">
            <span><strong>{summary.counts.wallets}</strong> wallets</span>
            <span><strong>{summary.counts.users}</strong> usuarios</span>
            <span><strong>{summary.counts.departments}</strong> deptos</span>
            <span><strong>{summary.counts.locations}</strong> ubic.</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-3 space-y-2">
        <input
          type="text"
          placeholder="Buscar..."
          value={filters.search || ''}
          onChange={(e) => updateFilter('search', e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
        />
        <div className="grid grid-cols-2 gap-2">
          <select
            value={filters.owner_type || ''}
            onChange={(e) => updateFilter('owner_type', e.target.value)}
            className="px-2 py-2 rounded-lg border border-slate-200 text-xs"
          >
            <option value="">Todos los tipos</option>
            <option value="user">👤 Usuarios</option>
            <option value="department">🏢 Departamentos</option>
            <option value="location">📍 Ubicaciones</option>
          </select>
          <select
            value={filters.currency_type_id || ''}
            onChange={(e) => updateFilter('currency_type_id', e.target.value)}
            className="px-2 py-2 rounded-lg border border-slate-200 text-xs"
          >
            <option value="">Todas las monedas</option>
            {currencyTypes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={filters.department_id || ''}
            onChange={(e) => updateFilter('department_id', e.target.value)}
            className="px-2 py-2 rounded-lg border border-slate-200 text-xs col-span-2"
          >
            <option value="">Todos los departamentos</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center justify-between pt-1">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={!!filters.only_with_balance}
              onChange={(e) => updateFilter('only_with_balance', e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            Solo con saldo
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => setGroupByOwner(!groupByOwner)}
              className="text-xs text-primary font-semibold"
            >
              {groupByOwner ? '📋 Vista plana' : '📁 Agrupar'}
            </button>
            {activeFiltersCount > 0 && (
              <button onClick={clearFilters} className="text-xs text-slate-500 font-semibold">
                Limpiar ({activeFiltersCount})
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingState />
      ) : balances.length === 0 ? (
        <EmptyState
          icon="💼"
          title="Sin resultados"
          description={activeFiltersCount > 0
            ? 'No se encontraron wallets con esos filtros.'
            : 'Aún no hay wallets con saldo.'}
        />
      ) : groupByOwner ? (
        <div className="space-y-2">
          {grouped.map((owner) => (
            <div key={`${owner.owner_type}:${owner.owner_id}`} className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-lg">{getOwnerIcon(owner.owner_type)}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{owner.owner_name}</p>
                    {owner.department_name && (
                      <p className="text-[10px] text-slate-500">{owner.department_name}</p>
                    )}
                  </div>
                </div>
                <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full flex-shrink-0">
                  {owner.wallets.length} 💼
                </span>
              </div>
              {owner.wallets.map((wallet) => (
                <div
                  key={wallet.wallet_id}
                  className="flex items-center justify-between py-1 border-t border-dashed border-slate-100"
                >
                  <span className="text-xs text-slate-500">{wallet.currency_name}</span>
                  <span className={`text-sm font-bold ${wallet.balance !== 0 ? 'text-primary' : 'text-slate-400'}`}>
                    {fc(wallet.balance)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-2 py-2 font-semibold text-slate-500">Tipo</th>
                <th className="text-left px-2 py-2 font-semibold text-slate-500">Nombre</th>
                <th className="text-right px-2 py-2 font-semibold text-slate-500">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {balances.map((row) => (
                <tr key={row.wallet_id} className="border-t border-slate-100">
                  <td className="px-2 py-2">{getOwnerIcon(row.owner_type)}</td>
                  <td className="px-2 py-2">
                    <div className="truncate max-w-[140px]">{row.owner_name}</div>
                    <div className="text-[10px] text-slate-400">{row.currency_name}</div>
                  </td>
                  <td className={`px-2 py-2 text-right font-bold ${row.balance !== 0 ? 'text-primary' : 'text-slate-400'}`}>
                    {fc(row.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}